import { ORDERED_SUPPORTED_LANGUAGES } from '@amzn/atoz-supported-languages';
import { DEFAULT_LOCALE } from '../constants';

export const LOCALE_STORAGE_KEY = 'myhr-lite-language-preferred';

export const RTL_LOCALES = ['ar-SA', 'fa-AF', 'ur-PK'];

export const LOCALE_FALLBACKS: Record<string, string> = {
    'en-AU': 'en-GB',
    'fr-BE': 'fr-FR',
    'hu-HU': 'en-US',
    'kn-IN': 'en-IN',
    'lt-LT': 'en-US',
    'mr-IN': 'hi-IN',
    'ms-MY': 'id-ID',
    'my-MM': 'en-US',
    'sv-SE': 'en-US',
    'th-TH': 'en-US',
    'nl-BE': 'nl-NL',
    'cs-CZ': 'cs',
    'sk-SK': 'sk',
};

const supportedLocales = (ORDERED_SUPPORTED_LANGUAGES || []).map((lang) => lang.locale);

/**
 * URL parameter name for language override.
 * This allows Taka kiosk configurations to set a default language via URL.
 * Example: https://lite.myhr.a2z.com/?site_code=GRU9&maask=true&lang=pt-BR
 */
export const LANG_URL_PARAM = 'lang';

/**
 * Detects locale from URL parameter (?lang=pt-BR).
 * This is the highest priority locale source, allowing Taka/kiosk admins
 * to configure default language per-site via URL.
 *
 * @returns The locale from URL if valid, undefined otherwise
 */
export function detectUrlLocale(): string | undefined {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get(LANG_URL_PARAM);

        if (langParam) {
            const matchedLocale = findSupportedLocale(langParam);
            if (matchedLocale) {
                return matchedLocale;
            }
        }
    } catch {
        // URL parsing failed (shouldn't happen in browser)
    }
    return undefined;
}

export function isRTLLocale(locale: string): boolean {
    return RTL_LOCALES.includes(locale);
}

export function applyDocumentDirection(locale: string): void {
    document.documentElement.dir = isRTLLocale(locale) ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
}

function findSupportedLocale(localeCode: string): string | undefined {
    if (supportedLocales.includes(localeCode)) {
        return localeCode;
    }

    if (LOCALE_FALLBACKS[localeCode] && supportedLocales.includes(LOCALE_FALLBACKS[localeCode])) {
        return LOCALE_FALLBACKS[localeCode];
    }

    const languageOnly = localeCode.split('-')[0];
    return supportedLocales.find((locale) => locale.startsWith(languageOnly + '-'));
}

export function detectBrowserLocale(): string {
    const browserLocale = navigator.language;
    const matchedLocale = findSupportedLocale(browserLocale);
    return matchedLocale || DEFAULT_LOCALE;
}

/**
 * Detects the appropriate locale using the following priority order:
 * 1. URL parameter (?lang=pt-BR) - Allows Taka/kiosk admins to set default
 * 2. localStorage - User's manually selected language preference
 * 3. Browser locale (navigator.language) - User's browser/OS setting
 * 4. DEFAULT_LOCALE (en-US) - Fallback
 *
 * Note: URL parameter takes highest priority because:
 * - Taka kiosk images don't propagate OS locale to Firefox's Accept-Language
 * - This allows per-site language configuration without browser changes
 * - See: Slack thread with ysato about GRU9 Portuguese default
 */
export function detectLocale(): string {
    // Priority 1: URL parameter (for Taka kiosk configuration)
    const urlLocale = detectUrlLocale();
    if (urlLocale) {
        return urlLocale;
    }

    // Priority 2: localStorage (user's manual selection)
    let storedLocale: string | null = null;
    try {
        storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing mode)
    }

    if (storedLocale && supportedLocales.includes(storedLocale)) {
        return storedLocale;
    }

    // Priority 3: Browser locale
    const browserLocale = navigator.language;
    const matchedLocale = findSupportedLocale(browserLocale);

    if (matchedLocale) {
        return matchedLocale;
    }

    // Priority 4: Default fallback
    return DEFAULT_LOCALE;
}

export function persistLocale(locale: string): void {
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
        // localStorage unavailable (private browsing mode)
    }
}

export function clearLocale(): void {
    try {
        localStorage.removeItem(LOCALE_STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing mode)
    }
}
